import { Button } from "components/Button/Button";
import { IconCollection } from "components/Icon/IconCollection";
import { SchemaFieldType } from "config/config";
import { CustomFilterValues } from "containers/AdvancedSearch/AdvancedSearch";
import { ValueLabelPair } from "containers/ExecutionFilter/interfaces";
import { ExistsFilterField } from "containers/Filter/FilterBuilder/components/ExistsFilterField";
import { FilterField } from "containers/Filter/FilterBuilder/components/FilterField";
import { FilterGroup } from "containers/Filter/FilterBuilder/components/FilterGroup";
import { get } from "lodash";
import { capitalizeFirstLetter } from "utils/utils";
import "../Filter.scss";
import { getMatchingSchema } from "../utils";

/**
 * Button component for deleting filter fields or groups.
 *
 * @param {Object} props - Component props
 * @param {() => void} props.onDelete - Callback function to handle deletion
 * @returns {JSX.Element} Delete button with trash icon
 */
export const DeleteFieldButton = ({ onDelete }: { onDelete: () => void }) => (
    <Button onClick={onDelete} icon={IconCollection.deleteOutline} variation="clear" />
);

interface FilterFieldProps {
    parentData: any;
    matchingFields: SchemaFieldType[];
    filteredFieldList: ValueLabelPair[];
    parentPath: string;
    isRoot?: boolean;
    setData: (data: CustomFilterValues) => void;
    formData: CustomFilterValues;
    filterGroupOperator?: string;
    deleteGroup?: () => void;
    activeTab?: string;
}

/**
 * Displays the filter group operator (AND/OR) as a visual indicator.
 * Only shown when there are multiple filter conditions.
 *
 * @param {Object} props - Component props
 * @param {boolean} props.isShown - Whether to show the operator
 * @param {string} props.operator - The operator to display (AND/OR)
 * @returns {JSX.Element | null} Operator display or null if not shown
 */
const FakeFilterGroupOperator = ({ isShown, operator }: { isShown: boolean; operator?: string }) =>
    isShown ? (
        <span className="filterGroupOperatorFake">{capitalizeFirstLetter(operator)}</span>
    ) : null;

/**
 * Main component for rendering filter fragments.
 * Recursively renders either individual filter fields or nested filter groups
 * based on the data structure. Handles both root-level and nested filter configurations.
 *
 * @param {FilterFieldProps} props - Component props
 * @returns {JSX.Element} Rendered filter fragment
 */
export const FilterFragment = ({
    parentData = [],
    matchingFields,
    filteredFieldList,
    parentPath,
    isRoot,
    formData,
    setData,
    filterGroupOperator,
    deleteGroup,
    activeTab,
}: FilterFieldProps) => {
    // Root level rendering - render as a single filter group
    if (isRoot) {
        return (
            <FilterGroup
                elementData={parentData}
                formData={formData}
                setData={setData}
                parentPath={parentPath}
                matchingFields={matchingFields}
                filteredFieldList={filteredFieldList}
                parentData={Object.values(formData)[0]}
                activeTab={activeTab}
            />
        );
    }

    // Render each item in the parent data array
    return parentData.map((value, index) => {
        // "Has related record" (reverse EXISTS) node: `{exists: {...}}` or its
        // NOT-EXISTS form `{not: {exists: {...}}}`. Checked BEFORE the field/group
        // discriminator since an exists node has neither a `field` nor an and/or key.
        if (value?.exists !== undefined || value?.not?.exists !== undefined) {
            return (
                <div style={{ position: "relative" }} key={`exists-${index}`}>
                    <FakeFilterGroupOperator
                        isShown={index > 0}
                        operator={filterGroupOperator}
                        key={`${parentPath}-${filterGroupOperator}[${index}]`}
                    />
                    <ExistsFilterField
                        elementData={value}
                        index={index}
                        parentPath={parentPath}
                        parentData={get(formData, parentPath)}
                        setData={setData}
                        formData={formData}
                        activeTab={activeTab}
                        deleteGroup={deleteGroup}
                        key={`${parentPath}[${index}]`}
                    />
                </div>
            );
        }
        // Check if this is a filter field (has a 'field' property)
        if (value?.field !== undefined) {
            // Extract the field name from the field value (handle cases like "field:subfield")
            const valueField = typeof value.field === "string" ? value.field.split(":")[0] : null;
            // Get the matching schema for this field
            const matchingSchema = getMatchingSchema(matchingFields, valueField);

            return (
                <div
                    style={{ position: "relative", marginBottom: "5px" }}
                    key={`${valueField}-${index}`}
                >
                    {/* Show operator (AND/OR) between filter fields */}
                    <FakeFilterGroupOperator
                        isShown={index > 0}
                        operator={filterGroupOperator}
                        key={`${parentPath}-${filterGroupOperator}[${index}]`}
                    />
                    {/* Render individual filter field */}
                    <FilterField
                        elementData={value}
                        matchingSchema={matchingSchema}
                        matchingFields={matchingFields}
                        index={index}
                        filteredFieldList={filteredFieldList}
                        parentPath={parentPath}
                        setData={setData}
                        formData={formData}
                        parentData={get(formData, parentPath)}
                        key={`${parentPath}[${index}]`}
                        deleteGroup={deleteGroup}
                    />
                </div>
            );
        } else {
            // This is a nested filter group (no 'field' property)
            return (
                <div style={{ position: "relative" }} key={index}>
                    {/* Show operator (AND/OR) between filter groups */}
                    <FakeFilterGroupOperator
                        isShown={index > 0}
                        operator={filterGroupOperator}
                        key={`${parentPath}-${filterGroupOperator}[${index}]`}
                    />
                    {/* Render nested filter group */}
                    <FilterGroup
                        elementData={value}
                        index={index}
                        formData={formData}
                        setData={setData}
                        parentPath={parentPath}
                        matchingFields={matchingFields}
                        filteredFieldList={filteredFieldList}
                        parentData={get(formData, parentPath)}
                        key={`${parentPath}[${index}]`}
                        activeTab={activeTab}
                    />
                </div>
            );
        }
    });
};
